// Shared Content Radar vocabulary.
//
// Every backend enum value the UI depends on lives here, so a backend rename
// touches this file only. The values match the API on
// supergrow-ai-api feat/content-radar: ContentRadarScan::STATUSES and STAGES,
// IntegrationConnection::STATUSES, and the ScanSerializer safe error codes.
//
// Connection management has no frontend role gate on purpose: the stored role
// string proved unreliable, and the backend already answers non-admins with a
// clear 403 message the pages surface as a notification.

// Content Radar shows only on the beta host and on local dev until it
// launches. Both the sidebar entry and the route check this, so a direct
// link on app.supergrow.ai lands on Not Found.
export const CONTENT_RADAR_HOSTS = ["beta.supergrow.ai", "localhost", "127.0.0.1"];

export const isContentRadarHost = (hostname = window.location.hostname) =>
  CONTENT_RADAR_HOSTS.includes(hostname);

export const SCAN_STATUS = {
  QUEUED: "queued",
  RUNNING: "running",
  COMPLETED: "completed",
  PARTIAL: "partial",
  NO_RESULTS: "no_results",
  FAILED: "failed",
  CANCELLED: "cancelled",
};

export const FINAL_SCAN_STATUSES = [
  SCAN_STATUS.COMPLETED,
  SCAN_STATUS.PARTIAL,
  SCAN_STATUS.NO_RESULTS,
  SCAN_STATUS.FAILED,
  SCAN_STATUS.CANCELLED,
];

// Final statuses that have a report worth fetching. `no_results` still returns
// a report body: an empty list plus the reason the API wrote for the user.
export const REPORT_READY_STATUSES = [
  SCAN_STATUS.COMPLETED,
  SCAN_STATUS.PARTIAL,
  SCAN_STATUS.NO_RESULTS,
];

export const isFinalScanStatus = (status) =>
  FINAL_SCAN_STATUSES.includes(status);

export const isReportReadyStatus = (status) =>
  REPORT_READY_STATUSES.includes(status);

// A note beside a finished scan, with the Badge variant it renders as.
// `completed` and `no_results` get none: their idea count already reads right.
// Without this, a failed scan says "0 ideas", exactly like a scan that ran
// fine and found nothing.
export const SCAN_STATUS_NOTES = {
  [SCAN_STATUS.PARTIAL]: { label: "Some sources failed", variant: "warning" },
  [SCAN_STATUS.FAILED]: { label: "Did not finish", variant: "destructive" },
  [SCAN_STATUS.CANCELLED]: { label: "Stopped", variant: "secondary" },
};

export const getScanStatusNote = (status) => SCAN_STATUS_NOTES[status] || null;

// ContentRadar::EmptyReportClassifier outcome types. The API returns one of
// these in `report.empty_state.type` when a finished scan produced no ideas.
export const EMPTY_REPORT_TYPE = {
  NO_REPORT: "no_report",
  NO_PUBLIC_ALIGNMENT: "no_public_alignment_report",
};

export const EMPTY_REPORT_TITLE = {
  [EMPTY_REPORT_TYPE.NO_REPORT]: "No Report",
  [EMPTY_REPORT_TYPE.NO_PUBLIC_ALIGNMENT]: "No public alignment report",
};

// An unknown or missing type reads as "No Report", so an added API type never
// leaves the page without a heading.
export const getEmptyReportTitle = (type) =>
  EMPTY_REPORT_TITLE[type] || EMPTY_REPORT_TITLE[EMPTY_REPORT_TYPE.NO_REPORT];

export const CONNECTION_STATUS = {
  ACTIVE: "active",
  PENDING: "pending",
  FAILED: "failed",
  DISCONNECTED: "disconnected",
};

// Backend scan stages, in the order the scan runs them.
export const SCAN_STAGES = [
  "list_artifacts",
  "choose_evidence_mode",
  "fetch_evidence",
  "decode_artifacts",
  "extract_claims",
  "cluster_themes",
  "validate_receipts",
  "find_public_signals",
  "align_public_signals",
  "classify_public_signals",
  "score_themes",
  "build_opportunities",
  "finalize",
];

// Every label the user reads stays in plain language. The keys keep the backend
// names, so nothing internal reaches the screen.
export const SCAN_STAGE_LABELS = {
  list_artifacts: "Opening your sources",
  choose_evidence_mode: "Picking the best notes",
  fetch_evidence: "Reading your notes",
  decode_artifacts: "Going through the details",
  extract_claims: "Pulling out what you said",
  cluster_themes: "Grouping the repeats",
  validate_receipts: "Double checking each one",
  find_public_signals: "Searching LinkedIn, X, and the web",
  align_public_signals: "Matching them to your topics",
  classify_public_signals: "Checking public popularity",
  score_themes: "Ranking the matches",
  build_opportunities: "Writing up your ideas",
  finalize: "Almost done",
};

// The backend stages map onto four steps the user can follow.
export const SCAN_STAGE_GROUPS = [
  {
    id: "private",
    label: "Reading your sources",
    description: "Radar goes through your notes from the days you picked.",
    stages: [
      "list_artifacts",
      "choose_evidence_mode",
      "fetch_evidence",
      "decode_artifacts",
    ],
  },
  {
    id: "themes",
    label: "Finding what you repeat",
    description: "A topic counts once it comes up in two different places.",
    stages: ["extract_claims", "cluster_themes", "validate_receipts"],
  },
  {
    id: "public",
    label: "Checking what is trending",
    description: "Radar looks at LinkedIn, X, and the web.",
    stages: [
      "find_public_signals",
      "align_public_signals",
      "classify_public_signals",
    ],
  },
  {
    id: "rank",
    label: "Picking the best ideas",
    description: "Only the strongest matches reach you.",
    stages: ["score_themes", "build_opportunities", "finalize"],
  },
];

export const getStageLabel = (stage) => SCAN_STAGE_LABELS[stage] || "Working";

export const getStageIndex = (stage) => SCAN_STAGES.indexOf(stage);

export const getStageGroupIndex = (stage) =>
  SCAN_STAGE_GROUPS.findIndex((group) => group.stages.includes(stage));

export const EVIDENCE_MODE = {
  TRANSCRIPT: "transcript",
  AI_SUMMARY: "ai_summary",
};

// Shown only inside the "why this ranks high" details, never as a page badge.
export const EVIDENCE_MODE_LABELS = {
  [EVIDENCE_MODE.TRANSCRIPT]: "Your full notes",
  [EVIDENCE_MODE.AI_SUMMARY]: "Your summaries",
};

// The proof is either a synthesis ("you said this five times") or one quote.
// The first release defaults to a synthesis, so no private line leaves the API.
export const RECEIPT_KIND = {
  SYNTHESIS: "synthesis",
  QUOTE: "quote",
};

export const SOURCE_FAMILY_LABELS = {
  linkedin: "LinkedIn",
  x: "X",
  search: "Web & news",
};

export const SIGNAL_STANCE = {
  SUPPORT: "support",
  OPPOSITION: "opposition",
};

// Score parts and maximums come from the API score calculator. Full notes can
// reach 100 points. Summaries can reach 90 points.
export const SCORE_PARTS = [
  { key: "receipt", label: "You have lived it", max: 30 },
  { key: "diversity", label: "Seen in several places", max: 25 },
  { key: "alignment", label: "Trending right now", max: 30 },
  { key: "freshness", label: "Still fresh", max: 15 },
];

export const SCORE_THRESHOLD = 80;

/** Turns a score into words, so the user never has to read a raw number. */
export const getMatchLabel = (score) => {
  if (score >= 90) return "Strong match";
  if (score >= 85) return "Good match";

  return "Worth a look";
};

export const SCAN_WINDOW_OPTIONS = [
  { days: 7, label: "Past week" },
  { days: 14, label: "Past 2 weeks" },
];

export const DEFAULT_SCAN_WINDOW_DAYS = 7;
export const CUSTOM_SCAN_WINDOW = "custom";
export const MAX_SCAN_WINDOW_DAYS = 30;

export const SCAN_POLL_INTERVAL_MS = 3000;

export const CONNECTION_VERIFY_INTERVAL_MS = 5000;

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDateInput = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

const calendarDayNumber = (value) => {
  const date = parseDateInput(value);
  if (!date) return null;

  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS;
};

export function toDateInputValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addCalendarDays(value, days) {
  const date = parseDateInput(value);
  if (!date) return "";

  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
}

export function validateCustomScanWindow(
  startDate,
  endDate,
  today = toDateInputValue()
) {
  if (!startDate || !endDate) return "Choose a start date and an end date.";

  const startDay = calendarDayNumber(startDate);
  const endDay = calendarDayNumber(endDate);
  const todayDay = calendarDayNumber(today);
  if (startDay === null || endDay === null) return "Choose valid dates.";
  if (endDay < startDay) return "The end date cannot be before the start date.";
  if (endDay > todayDay) return "The end date cannot be in the future.";
  if (endDay - startDay + 1 > MAX_SCAN_WINDOW_DAYS) {
    return `Choose a range of ${MAX_SCAN_WINDOW_DAYS} days or less.`;
  }

  return null;
}

/** Turns a look-back choice into the ISO window the scan API expects. */
export function resolveScanWindow(windowDays, now = Date.now()) {
  return {
    windowStart: new Date(now - windowDays * DAY_MS).toISOString(),
    windowEnd: new Date(now).toISOString(),
  };
}

/** Turns local calendar dates into the exact ISO window the scan API expects. */
export function resolveCustomScanWindow(startDate, endDate, now = Date.now()) {
  const start = parseDateInput(startDate);
  const end = parseDateInput(endDate);
  if (!start || !end) return { windowStart: undefined, windowEnd: undefined };

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return {
    windowStart: start.toISOString(),
    windowEnd: new Date(Math.min(end.getTime(), now)).toISOString(),
  };
}
